<template>
  <div class="studio-product-content">
    <StudioProductPlaylists :showOnlyFeatured="true" />

    <div class="row">
      <div class="col-12">
        <HMSearch v-model="filterBy.q" @update:modelValue="filterMedia" />
      </div>
    </div>
    <div class="row mt-2 d-none d-md-flex">
      <div
        class="col-12 d-flex flex-row align-items-center justify-content-start flex-nowrap"
      >
        <select class="form-select" v-model="filterBy.mediaType" @change="filterMedia">
          <option :value="null">{{
            $t('PRODUCTS.LABELS.FILTER_ALL_TYPES')
          }}</option>
          <option
            v-for="mt in mediaTypes"
            :value="mt.value"
            :key="mt.value"
          >
            {{ $t(mt.displayCode) }}
          </option>
        </select>
        <select
          v-model="filterBy.instructor"
          class="form-select ms-2"
          @change="filterMedia"
        >
          <option :value="null">{{
            $t('PRODUCTS.LABELS.FILTER_INSTRUCTORS')
          }}</option>
          <option
            v-for="instructor in product.instructors"
            :value="instructor.id"
            :key="instructor.id"
          >
            {{ instructor.name }}
          </option>
        </select>
        <select
          v-for="category in getProductFilterOptions"
          :key="category.category_id"
          v-model="filterBy.filterByCategories[category.category_id]"
          class="form-select ms-2"
          @change="filterMedia"
        >
          <option :value="null">{{
            category.name
          }}</option>
          <option
            v-for="option in category.option_list"
            :value="option.id"
            :key="option.id"
          >
            {{ option.name }}
          </option>
        </select>
      </div>
    </div>

    <div class="row mt-2 d-md-none">
      <div
        class="col-12 d-flex flex-row align-items-center justify-content-start flex-wrap"
      >
        <select
          class="form-select mt-1 w-100"
          v-model="filterBy.mediaType"
          @change="filterMedia"
        >
          <option :value="null">{{
            $t('PRODUCTS.LABELS.FILTER_ALL_TYPES')
          }}</option>
          <option
            v-for="mt in mediaTypes"
            :value="mt.value"
            :key="mt.value"
          >
            {{ $t(mt.displayCode) }}
          </option>
        </select>
        <select
          class="form-select mt-1 w-100"
          v-model="filterBy.instructor"
          @change="filterMedia"
        >
          <option :value="null">{{
            $t('PRODUCTS.LABELS.FILTER_INSTRUCTORS')
          }}</option>
          <option
            v-for="instructor in instructors"
            :value="instructor.id"
            :key="instructor.id"
          >
            {{ instructor.name }}
          </option>
        </select>
        <select
          class="form-select mt-1 w-100"
          v-for="category in getProductFilterOptions"
          :key="category.category_id"
          v-model="filterBy.filterByCategories[category.category_id]"
          @change="filterMedia"
        >
          <option :value="null">{{
            category.name
          }}</option>
          <option
            v-for="option in category.option_list"
            :value="option.id"
            :key="option.id"
          >
            {{ option.name }}
          </option>
        </select>
      </div>
    </div>

    <HMPaginatedTable
      v-if="query"
      ref="product-content-table"
      :api="productStore.getProductMediaPaginated"
      :query="query"
      name="product-content-table"
      :pageInQuery="true"
      :perPageFixed="12"
    >
      <template slot-scope="{ data }" #default="{ data }">
        <div class="row mt-4">
          <div
            class="column col-6 col-md-4 col-lg-3 mt-2"
            :class="{ review: review }"
            v-for="media in data.res.results"
            :key="media.id"
          >
            <NuxtLink
              class="undecorated-router-link"
              :to="
                getCorrectRoute({
                  media_id: media.id,
                  isMediaLocked: media.media.locked,
                })
              "
            >
              <StudioMediaCard :product="product" :media="media" />
            </NuxtLink>
          </div>
        </div>
      </template>
    </HMPaginatedTable>

    <div class="row" v-if="showEmpty">
      <div class="col-12 text-center py-5">
        {{ $t('PRODUCTS.TEXT.NO_MEDIA_FOUND') }}
      </div>
    </div>
    <div class="row" v-if="isTableLoading">
      <div class="col-12 text-center py-5">
        <HMSpinner />
      </div>
    </div>
    <div class="page-bottom-filler"></div>
  </div>
</template>

<script lang="ts">
import { errorToMessage } from '@/utils/errors';
import { mapState } from 'pinia';
import { useStudioStore, useUserStore, useProductStore, useFiltersStore } from '@/stores';
import StudioMediaCard from '@/components/products/StudioMediaCard.vue';
import { mediaTypes } from '@/utils/products';
import RefreshData from '@/mixins/refreshData';
import StudioProductPlaylists from '@/components/products/StudioProductPlaylists.vue';

export default {
  name: 'StudioProductContent',
  mixins: [RefreshData],
  components: {
    StudioMediaCard,
    StudioProductPlaylists,
  },
  data() {
    return {
      isLoading: true,
      mediaTypes: [],
      filterBy: {
        q: null,
        mediaType: null,
        instructor: null,
        filterByCategories: {},
      },
      query: null,
    };
  },
  props: {
    review: {
      type: Boolean,
      default: false,
    },
  },
  setup() {

    const productStore = useProductStore();
    return {
      productStore
    };
  },
  computed: {
    ...mapState(useProductStore, {
      product: 'product',
      productMedia: 'productMedia',
      instructors: 'studioInstructors',
    }),
    studioURL() {
      return this.$studioURL;
    },
    getProductFilterOptions() {
      return this.product?.categories || [];
    },
    productId() {
      return this.$route?.params?.product_id;
    },
    table() {
      return this.$refs['product-content-table'];
    },
    showEmpty() {
      if (this.table) {
        return this.table.perPage === 0 && !this.table.isLoading;
      }
      return false;
    },
    isTableLoading() {
      if (this.table) {
        return this.table.isLoading;
      }
      return false;
    },
  },
  methods: {
    filterOptionsIds() {
      if (this.filterBy.filterByCategories) {
        return Object.values(this.filterBy.filterByCategories)
          .map(r => r)
          .filter(r => r);
      }
      return [];
    },
    filterMedia() {
      this.query = this.searchQuery();
    },
    searchQuery() {
      return {
        studioURL: this.studioURL,
        productId: this.productId,
        options: this.filterOptionsIds(),
        instructor: this.filterBy.instructor,
        mediaType: this.filterBy.mediaType,
        q: this.filterBy.q,
      };
    },
    getCorrectRoute({ media_id, isMediaLocked }) {
      if (isMediaLocked) {
        return {
          name: 'studio-product-checkout',
          params: { product_id: this.$route.params.product_id },
        };
      }
      return {
        name: 'studio-media',
        params: { media_id },
      };
    },
    async loadData() {
      const studioURL = this.studioURL;
      try {
        this.isLoading = true;
        await Promise.all([
          useProductStore().getStudioInstructors({ studioURL }),
          useProductStore().getFilterCategories({ studioURL }),
        ]);
        const filterCategories = {};
        this.getProductFilterOptions.forEach(category => {
          filterCategories[category.category_id] = null;
        });
        this.filterBy.filterByCategories = filterCategories;
      } catch (error) {
        this.$notify({
          group: 'app',
          type: 'error',
          title: 'Failed to load information',
          text: errorToMessage(error.response, `Request failed: ${error}`),
        });
      } finally {
        this.isLoading = false;
      }
    },
  },
  async mounted() {
    await this.loadData();
    this.mediaTypes.push(...mediaTypes());
    const filter = await useFiltersStore().getFilter('studio-product-content');
    if (filter && filter.productId === this.productId) {
      this.filterBy = filter.filterBy;
    }
    this.query = this.searchQuery();
  },
  beforeUnmount() {
    const filter = {
      productId: this.productId,
      filterBy: JSON.parse(JSON.stringify(this.filterBy)),
    }
    useFiltersStore().setFilter({key: 'studio-product-content', filter  });
  },
};
</script>

<style scoped>
.review {
  pointer-events: none;
}
</style>
